import { useCallback, useState } from "react"

//#region  [main]

/**
 * NFCカードリーダーを接続し読み取ります。
 * SONY PaSoRi RC-S380専用です。
 * コマンドは以下ページをそのままコピペしてるため確認必須です
 *  https://github.com/saturday06/webusb-felica/blob/gh-pages/demo.html
 *
 * @returns
 */
export const useRFC380Reader = (): [
  USBDevice | undefined,
  TSelectReader,
  TOpenReaderSession,
  TCloseReaderSession,
  TPollingFelica
] => {
  const [USBDeviceState, setUSBDeviceState] = useState<USBDevice>()
  const [USBConfiguration, setUSBConfiguration] = useState<USBConfiguration>()
  const [interfaceNumber, setInterfaceNumber] = useState<number>()
  const [endpointIn, setEndpointIn] = useState<USBEndpoint>()
  const [endpointOut, setEndpointOut] = useState<USBEndpoint>()

  /**
   * usbDevice初期化処理
   * @param options
   */
  const getUSBDevice = useCallback(
    async (options?: USBDeviceRequestOptions | undefined) => {
      const attachedDevices = await navigator.usb.getDevices()

      let deviceCount = 0
      if (attachedDevices.length > 0) {
        for (const device of attachedDevices) {
          const targetDevice = filters.find(
            (d) =>
              d.productId === device.productId && d.vendorId === device.vendorId
          )
          if (targetDevice != null) {
            ++deviceCount
            if (deviceCount <= 1) {
              setDeviceState(device)
            }
          }
        }
      }

      // 1件確実に定義できない場合、ユーザーに選択させる
      if (deviceCount !== 1) {
        const newDevice = await navigator.usb.requestDevice({
          filters,
          ...options,
        })
        setDeviceState(newDevice)
      }
    },
    []
  )

  /**
   * 取得したUSBDeviceを各stateに格納
   * @param device
   */
  const setDeviceState = (device: USBDevice) => {
    setUSBDeviceState(device)
    // 必ずある前提
    if (device.configuration == null) {
      throw new Error("configurationが見つかりません")
    }
    setUSBConfiguration(device.configuration)
    // TODO: RC-S380は決め打ち
    setInterfaceNumber(0)

    setEndpointIn(
      getEndpoint(
        device.configuration,
        "in",
        device.configuration.interfaces[0].interfaceNumber,
        "bulk"
      )
    )
    setEndpointOut(
      getEndpoint(
        device.configuration,
        "out",
        device.configuration.interfaces[0].interfaceNumber,
        "bulk"
      )
    )
  }

  /**
   * コマンドをリーダーに送信します
   */
  const send = useCallback(
    async (argData: number[], argProc = "") => {
      if (USBDeviceState == null || endpointOut == null) return

      // const res =
      await USBDeviceState.transferOut(
        endpointOut.endpointNumber,
        new Uint8Array(argData)
      )

      // console.log(res)
      await sleep(10)
    },
    [USBDeviceState, endpointOut]
  )

  /**
   * リーダーからデータを受信します
   */
  const receive = useCallback(
    async (packetSize?: number) => {
      if (USBDeviceState == null || endpointIn == null) return

      const res = await USBDeviceState.transferIn(
        endpointIn.endpointNumber,
        packetSize ?? endpointIn.packetSize
      )
      await sleep(20)
      if (res.data == null) return console.log("data undefined")

      // const resStr =
      dataviewToArray(res.data)
      // console.log(res)
      // console.log(
      //   "RECV Status[" +
      //     res.status +
      //     "]<br/>&nbsp;&nbsp;&nbsp;<-- [ " +
      //     resStr +
      //     "]<br/>"
      // )

      return res
    },
    [USBDeviceState, endpointIn]
  )

  /**
   * デバイスのセッションをスタートします。
   * @returns
   */
  const openSession = useCallback(async () => {
    if (
      USBDeviceState == null ||
      USBConfiguration == null ||
      interfaceNumber == null
    )
      return

    try {
      await USBDeviceState.open()
      // configurationの数字は決め打ち
      await USBDeviceState.selectConfiguration(1)

      await USBDeviceState.claimInterface(interfaceNumber)
    } catch (error) {
      console.log(error)
      getUSBDevice()
    }

    return
  }, [USBConfiguration, USBDeviceState, getUSBDevice, interfaceNumber])

  /**
   * デバイスとのセッションをクローズします
   */
  const closeSession = useCallback(async () => {
    if (USBDeviceState == null || interfaceNumber == null) return

    await USBDeviceState.releaseInterface(interfaceNumber)
    await USBDeviceState.close()
  }, [USBDeviceState, interfaceNumber])

  const polling = useCallback(async () => {
    // console.log(
    //   "SEND --> [ " + arrayToHex(new Uint8Array(RCS380Command.ack)) + "]"
    // )

    await send(RCS380Command.ack)
    // console.log(
    //   "SEND --> [ " +
    //     arrayToHex(new Uint8Array(RCS380Command.commandType01)) +
    //     "]"
    // )
    await send(RCS380Command.commandType01)
    await receive(6)
    await receive(13)

    // Felica対応

    // console.log(
    //   "SEND --> [ " + arrayToHex(new Uint8Array(RCS380Command.SwitchRF)) + "]"
    // )
    await send(RCS380Command.SwitchRF)
    await receive(6)
    await receive(13)

    // console.log(
    //   "SEND --> [ " + arrayToHex(new Uint8Array(RCS380Command.SwitchRF)) + "]"
    // )
    await send(RCS380Command.SwitchRF)
    await receive(6)
    await receive(13)

    // console.log(
    //   "SEND --> [ " + arrayToHex(new Uint8Array(RCS380Command.InSetRF1)) + "]"
    // )
    await send(RCS380Command.InSetRF1)
    await receive(6)
    await receive(13)

    // console.log(
    //   "SEND --> [ " + arrayToHex(new Uint8Array(RCS380Command.SwitchRF)) + "]"
    // )
    await send(RCS380Command.InSetProtocol1)
    await receive(6)
    await receive(13)

    // console.log(
    //   "SEND --> [ " +
    //     arrayToHex(new Uint8Array(RCS380Command.InSetProtocol2)) +
    //     "]"
    // )
    await send(RCS380Command.InSetProtocol2)
    await receive(6)
    await receive(13)

    // console.log(
    //   "SEND --> [ " +
    //     arrayToHex(new Uint8Array(RCS380Command.InCommRFPolling)) +
    //     "]"
    // )
    await send(RCS380Command.InCommRFPolling)
    await receive(6)
    const felicaRes = await receive(37)
    if (felicaRes != null && felicaRes.data != null) {
      const dataStr = dataviewToArray(felicaRes.data).slice(17, 25)
      if (dataStr.length > 0) {
        return dataStr
      }
    }

    // Mifare対応
    // console.log(
    //   "SEND --> [ " + arrayToHex(new Uint8Array(RCS380Command.SwitchRF)) + "]"
    // )
    await send(RCS380Command.SwitchRF)
    await receive(6)
    await receive(13)

    // console.log(
    //   "SEND --> [ " +
    //     arrayToHex(new Uint8Array(RCS380Command.MIFARE_TYPE_A.command1)) +
    //     "]"
    // )
    await send(RCS380Command.MIFARE_TYPE_A.command1)
    await receive(6)
    await receive(13)

    // console.log(
    //   "SEND --> [ " +
    //     arrayToHex(new Uint8Array(RCS380Command.MIFARE_TYPE_A.command2)) +
    //     "]"
    // )
    await send(RCS380Command.MIFARE_TYPE_A.command2)
    await receive(6)
    await receive(13)

    // console.log(
    //   "SEND --> [ " +
    //     arrayToHex(new Uint8Array(RCS380Command.MIFARE_TYPE_A.command3)) +
    //     "]"
    // )
    await send(RCS380Command.MIFARE_TYPE_A.command3)
    await receive(6)
    await receive(13)

    // console.log(
    //   "SEND --> [ " +
    //     arrayToHex(new Uint8Array(RCS380Command.MIFARE_TYPE_A.command4)) +
    //     "]"
    // )
    await send(RCS380Command.MIFARE_TYPE_A.command4)
    await receive(6)
    await receive(20)

    // console.log(
    //   "SEND --> [ " +
    //     arrayToHex(new Uint8Array(RCS380Command.MIFARE_TYPE_A.command5)) +
    //     "]"
    // )
    await send(RCS380Command.MIFARE_TYPE_A.command5)
    await receive(6)
    await receive(13)

    // console.log(
    //   "SEND --> [ " +
    //     arrayToHex(new Uint8Array(RCS380Command.MIFARE_TYPE_A.command6)) +
    //     "]"
    // )
    await send(RCS380Command.MIFARE_TYPE_A.command6)
    await receive(6)
    await receive(13)

    // console.log(
    //   "SEND --> [ " +
    //     arrayToHex(new Uint8Array(RCS380Command.MIFARE_TYPE_A.command7)) +
    //     "]"
    // )
    await send(RCS380Command.MIFARE_TYPE_A.command7)
    await receive(6)
    let mifareTypeARes = await receive(22)
    if (mifareTypeARes != null && mifareTypeARes.data != null) {
      const dataStr = dataviewToArray(mifareTypeARes.data).slice(15, 19)
      if (dataStr.length > 2) {
        return dataStr
      }
    }

    console.log("%c読取失敗!", "font-size:3rem; color:red;")
  }, [receive, send])

  return [USBDeviceState, getUSBDevice, openSession, closeSession, polling]
}

//#endregion

//#region [type]

export type TEndpoints = {
  in?: USBEndpoint
  out?: USBEndpoint
}

export type TSelectReader = (
  options?: USBDeviceRequestOptions | undefined
) => Promise<void>
export type TOpenReaderSession = () => Promise<unknown>
export type TCloseReaderSession = () => Promise<unknown>

export type TPollingFelica = () => Promise<number[] | null | undefined>

//#endregion

//#region [method]

// /**
//  * リクエストヘッダー作成
//  * 参考: https://qiita.com/Aruneko/items/aebb75feca5bed12fe32
//  *
//  * @param command
//  * @param seqNumber
//  * @returns
//  */
// const addReqHeader = (
//   command: Uint8Array,
//   seqNumber: React.MutableRefObject<number>
// ) => {
//   const dataLen = command.length

//   let retVal = new Uint8Array(11 + dataLen)

//   retVal[0] = 0x00 //固定値
//   retVal[1] = 0x00 //固定値
//   retVal[2] = 0xff //固定値
//   retVal[3] = 0xff //固定値
//   retVal[4] = 0xff //固定値
//   retVal[5] = 255 & dataLen // length をリトルエンディアン
//   retVal[6] = (dataLen >> 8) & 255 // lengthの2行目
//   // CRC-CITTによるチェックサムだが以下の単純式で求められる
//   const checkSum = (255 - command.sum()) % 256
//   retVal[7] = checkSum // checkSum

//   0 !== dataLen && retVal.set(command, 11) // コマンド追加

//   return retVal
// }

/**
 * Dataviewから配列への変換
 * @param argData
 * @returns
 */
const dataviewToArray = (argData: DataView) => {
  let retVal = []
  for (let i = argData.byteOffset; i < argData.byteLength; ++i) {
    retVal.push(argData.getUint8(i))
  }
  return retVal
}

/**
 * 配列の要素を16進数で返します。
 * @param argData
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const arrayToHex = (argData: Uint8Array) => {
  let retVal = ""
  for (let val of argData) {
    let str = val.toString(16)
    str = val < 0x10 ? "0" + str : str
    retVal += str.toUpperCase() + " "
  }
  return retVal
}

/**
 * スリープ処理
 * @param msec
 * @returns
 */
export const sleep = async (msec: number) => {
  return new Promise((resolve) => setTimeout(resolve, msec))
}

const getEndpoint = (
  configuration: USBConfiguration,
  direction: USBDirection,
  interfaceNumber: number,
  type: USBEndpointType
): USBEndpoint | undefined => {
  const USBInterface = configuration.interfaces[interfaceNumber]

  return USBInterface.alternate.endpoints.find(
    (e) => e.direction === direction && e.type === type
  )
}

//#endregion

//#region [constant]

const filters = [
  { vendorId: 1356, productId: 1729 }, // SONY PaSoRi RC-S380/S
  { vendorId: 1356, productId: 1731 }, // SONY PaSoRi RC-S380/P
]

// RC-S380 コマンド
export const RCS380Command = {
  /** ack */
  ack: [0x00, 0x00, 0xff, 0x00, 0xff, 0x00],
  /** SetCommandType 01 */
  commandType01: [
    0x00, 0x00, 0xff, 0xff, 0xff, 0x03, 0x00, 0xfd, 0xd6, 0x2a, 0x01, 0xff,
    0x00,
  ],
  /** SwitchRF 00 */
  SwitchRF: [
    0x00, 0x00, 0xff, 0xff, 0xff, 0x03, 0x00, 0xfd, 0xd6, 0x06, 0x00, 0x24,
    0x00,
  ],
  /** InSetRF 01010f01 */
  InSetRF1: [
    0x00, 0x00, 0xff, 0xff, 0xff, 0x06, 0x00, 0xfa, 0xd6, 0x00, 0x01, 0x01,
    0x0f, 0x01, 0x18, 0x00,
  ],
  /** InSetProtocol 00180101020103000400050006000708080009000a000b000c000e040f001000110012001306 */
  InSetProtocol1: [
    0x00, 0x00, 0xff, 0xff, 0xff, 0x28, 0x00, 0xd8, 0xd6, 0x02, 0x00, 0x18,
    0x01, 0x01, 0x02, 0x01, 0x03, 0x00, 0x04, 0x00, 0x05, 0x00, 0x06, 0x00,
    0x07, 0x08, 0x08, 0x00, 0x09, 0x00, 0x0a, 0x00, 0x0b, 0x00, 0x0c, 0x00,
    0x0e, 0x04, 0x0f, 0x00, 0x10, 0x00, 0x11, 0x00, 0x12, 0x00, 0x13, 0x06,
    0x4b, 0x00,
  ],
  /** InSetProtocol 0018 */
  InSetProtocol2: [
    0x00, 0x00, 0xff, 0xff, 0xff, 0x04, 0x00, 0xfc, 0xd6, 0x02, 0x00, 0x18,
    0x10, 0x00,
  ],
  /**
   * InCommRF 6e000600ffff0100
   * pollingコマンド?
   */
  InCommRFPolling: [
    0x00, 0x00, 0xff, 0xff, 0xff, 0x0a, 0x00, 0xf6, 0xd6, 0x04, 0x6e, 0x00,
    0x06, 0x00, 0xff, 0xff, 0x01, 0x00, 0xb3, 0x00,
  ],
  // TODO:かなり未知のコマンドなので調査必須
  MIFARE_TYPE_A: {
    command1: [
      0x00, 0x00, 0xff, 0xff, 0xff, 0x06, 0x00, 0xfa, 0xd6, 0x00, 0x02, 0x03,
      0x0f, 0x03, 0x13, 0x00,
    ],
    command2: [
      0x00, 0x00, 0xff, 0xff, 0xff, 0x28, 0x00, 0xd8, 0xd6, 0x02, 0x00, 0x18,
      0x01, 0x01, 0x02, 0x01, 0x03, 0x00, 0x04, 0x00, 0x05, 0x00, 0x06, 0x00,
      0x07, 0x08, 0x08, 0x00, 0x09, 0x00, 0x0a, 0x00, 0x0b, 0x00, 0x0c, 0x00,
      0x0e, 0x04, 0x0f, 0x00, 0x10, 0x00, 0x11, 0x00, 0x12, 0x00, 0x13, 0x06,
      0x4b, 0x00,
    ],
    command3: [
      0x00, 0x00, 0xff, 0xff, 0xff, 0x0c, 0x00, 0xf4, 0xd6, 0x02, 0x01, 0x00,
      0x02, 0x00, 0x05, 0x01, 0x00, 0x06, 0x07, 0x07, 0x0b, 0x00,
    ],
    command4: [
      0x00, 0x00, 0xff, 0xff, 0xff, 0x05, 0x00, 0xfb, 0xd6, 0x04, 0x36, 0x01,
      0x26, 0xc9, 0x00,
    ],
    command5: [
      0x00, 0x00, 0xff, 0xff, 0xff, 0x06, 0x00, 0xfa, 0xd6, 0x02, 0x04, 0x01,
      0x07, 0x08, 0x14, 0x00,
    ],
    command6: [
      0x00, 0x00, 0xff, 0xff, 0xff, 0x06, 0x00, 0xfa, 0xd6, 0x02, 0x01, 0x00,
      0x02, 0x00, 0x25, 0x00,
    ],
    command7: [
      0x00, 0x00, 0xff, 0xff, 0xff, 0x06, 0x00, 0xfa, 0xd6, 0x04, 0x36, 0x01,
      0x93, 0x20, 0x3c, 0x00,
    ],
  },
}

//#endregion
